export const DEFAULT_PERSONA_TARGET = 'over100';

export interface PersonaProvenance {
  profileCovered: number; dialogueCovered: number;
  window: { start: string; end: string } | null;
  method: 'profile_then_complete_dialogue' | 'clustered_profile' | 'device_configuration' | 'canonical_turn_count_current_timezone';
  status: 'disabled' | 'pending' | 'failed' | 'ready';
  calibration?: 'uncalibrated';
  outsidePilot?: number;
}
export interface PersonaGaps {
  noProfile: number; noFieldEvidence: number; lowConfidence: number;
  conflict: number; pending: number; failed: number;
}
export interface PersonaCoverageDiagnostics {
  version: 1;
  stages: { devices: number; withCountedTurns: number; primaryUsers: number; profiles: number; classified: number };
  identity: { noCountedTurns: number; identityUnresolved: number; rankingUncertain: number; attributableTurnRatio: number | null };
  completeness: { fields: string[]; total: number; distribution: { knownFields: number; count: number }[] };
}
export interface PersonaDimension {
  key: string; title: string; group: string; unit: '人' | '台'; total: number;
  covered: number; unknown: number; conflict: number; multiple?: boolean;
  status?: 'ready' | 'pending' | 'unavailable'; pending?: number; lowConfidence?: number; failed?: number;
  provenance?: PersonaProvenance; gaps?: PersonaGaps;
  // trusted / inferred 只来自原文复核结果：同一人同一值只计一次，二者之和等于 count。
  definition: string; rows: { label: string; count: number; trusted?: number; inferred?: number }[];
  /** 性别与年龄段按人合并：自述几人、人脸推断几人。 */
  sources?: { selfReported: number; inferred: number };
}
export interface PersonaTargetCoverage {
  version: 1;
  criterion: { kind: 'device_canonical_turns'; operator: '>'; threshold: 100 };
  // 除 visibleDevices 是 cohort 交集，其余指标均以全部目标设备为范围。
  targetDevices: number; visibleDevices: number;
  identity: { resolved: number; rankingUncertain: number; unresolved: number };
  analyzableSpeakers: number; devicesWithAttributedEvidence: number;
}
export interface PersonaSnapshot {
  source: 'synthetic' | 'measured';
  coverageDiagnostics?: PersonaCoverageDiagnostics;
  targetCoverage?: PersonaTargetCoverage;
  speakerEvidence?: SpeakerEvidence;
  verifiedSpeakerEvidence?: VerifiedSpeakerEvidence;
  meta?: { dialogueEnabled?: boolean; dialogueSourceStatuses?: Record<string, number>; asOf: string; observedAt: string; cohortObservedAt: string; selectionWindow: { start: string | null; end: string }; timeWindow?: { start: string | null; end: string }; reportTimezone?: string; timeMethod?: 'canonical_turn_count_current_timezone'; primaryMethod: string; status: string; scopeNote: string };
  unresolvedReasons?: Record<string, number>; devices: number; primaryUsers: number; unresolved: number;
  profiles: number; unchanged?: number; refreshed?: number; classified?: number; failed?: number; pending: number;
  dimensions: PersonaDimension[];
}

export type SpeakerFieldState = 'accepted' | 'unknown' | 'dated' | 'pending' | 'failed' | 'privacy' | 'needs_calibration' | 'conflict' | 'not_started' | 'stale' | 'no_user_speech';
export type SpeakerProcessingState = 'reviewed' | 'pending_principal_review' | 'partial' | 'not_started' | 'stale' | 'pending' | 'failed' | 'privacy' | 'no_user_speech';
export interface SpeakerEvidenceField {
  key: string; total: number; multiple: boolean; accepted: number; reviewed: number; crosschecked: number; pendingPrincipalReview: number; principalReviewed?: number;
  states: Record<SpeakerFieldState, number>; coveredDevices: number; deviceTotal: number;
  rows: { label: string; count: number }[];
}
export interface SpeakerEvidenceGroup {
  targetDevices: number; speakers: number; devicesWithAttributedEvidence: number;
  noAttributedSpeakerDevices: number; staleDevices: number;
  deviceStates: Record<Exclude<SpeakerProcessingState, 'reviewed'> | 'all_attributed_speakers_reviewed' | 'no_attributed_speakers', number>;
  speakerStates: Record<SpeakerProcessingState, number>; fields: SpeakerEvidenceField[];
}
export interface SpeakerEvidenceReady {
  version: 1; status: 'available' | 'not_started';
  criterion: { kind: 'device_canonical_turns'; operator: '>'; threshold: 100 };
  unit: 'per_device_canonical_terminal_identity'; acceptedMeaning: string; reviewedMeaning: string;
  groundTruth: false; primaryDimensionsUnchanged: true; asOf: string | null;
  allTargets: SpeakerEvidenceGroup; visible: SpeakerEvidenceGroup;
  frozenRound?: { scope: 'frozen_round_all_targets'; asOf: string; createdAt: string; allTargets: SpeakerEvidenceGroup } | null;
}
export type SpeakerEvidence = SpeakerEvidenceReady | { version: 1; status: 'unavailable' };


export interface PersonaBaseReviewCounts {
  totalConclusions: number;
  resolvedConclusions: number;
  reviewedSubjects: number;
  unknownAttributionConclusions: number;
  rejectedConclusions: number;
  qualifiedConclusions: number;
  conflictConclusions: number;
}
export interface PersonaBaseReview {
  version: 1;
  status: 'complete';
  artifactHash: string;
  protocolHash: string;
  reviewedAt: string;
}
export interface ReviewedScopedRow {
  polarity: 'support' | 'refute';
  label: string;
  attribution: 'primary' | 'device_only' | 'uncertain' | 'other';
  basis: 'explicit_self' | 'contextual_inference' | 'observed_pattern';
  temporal: 'current' | 'past' | 'intended' | 'unspecified';
  scope: 'single_event' | 'recurring' | 'stable' | 'historical' | 'intended';
  count: number;
}
export interface VerifiedEvidenceField {
  baseReview?: PersonaBaseReviewCounts & { scopedRows: ReviewedScopedRow[] };
  key: string; total: number; deviceTotal: number; known: number; coveredDevices: number; producerVersions?: string[];
  sourceComplete: number; reviewComplete: number; unknown: number; conflict: number;
  currentKnown?: number;
  lastRecordedValues?: { label: string; count: number; firstDay: string; lastDay: string }[];
  conflictedValues?: { label: string; count: number }[];
  pendingPrincipal: number; blindCounterevidencePending: number;
  confidence: null; accepted: false;
  expectedChunks: number; processedChunks: number; reviewedChunks: number; failedChunks: number; missingChunks: number; omissionChunks: number; uncertainCoverageChunks: number; reviewFailedChunks: number; reviewPendingChunks: number; reviewNotSampledChunks: number; reviewSampledChunks: number; blindNotSampledChunks: number; blindSampledChunks: number;
  blindStates: Record<string, number>;
  distribution?: { label: string; count: number; trusted: number; inferred: number }[];
  layers: Record<'directValues' | 'inferredValues' | 'observedValues', { label: string; count: number }[]>;
  pendingLayers: Record<'directValues' | 'inferredValues', { label: string; count: number }[]>;
  history: { label: string; basis: string; temporal: string; polarity: string; freshness: string; count: number; firstDay: string; lastDay: string }[];
  /** 只在性别与年龄段：每人有单一自述用自述，否则用人脸推断；trusted 是自述，inferred 是人脸推断。 */
  merged?: VerifiedMergedField;
}
export interface VerifiedMergedField {
  total: number; selfReported: number; inferred: number; unknown: number; inferredMethod: 'face_images_model_inference';
  rows: { label: string; count: number; trusted: number; inferred: number }[];
}
export interface VerifiedEvidenceGroup {
  baseReview?: PersonaBaseReviewCounts;
  targetDevices: number; speakers: number; confirmedPrimaryDevices?: number; candidatePrimaryDevices?: number; primaryUnresolvedDevices?: number; identityUncertainDevices?: number; confirmed?: VerifiedEvidenceGroup; candidates?: VerifiedEvidenceGroup; fullyReviewedDevices: number; fullyProcessedDevices?: number; noAttributedSpeakerDevices: number;
  speakerStates: Record<string, number>; fields: VerifiedEvidenceField[]; facets?: PersonaFacets;
}
export interface PersonaFacetReviewCounts { reviewedGroups: number; keptGroups: number; partialGroups: number; droppedGroups: number; droppedClaims: number }
export interface PersonaFacetReview { version: 1; status: 'complete'; artifactHash: string; protocolHash: string; reviewedAt: string }
export interface VerifiedSpeakerEvidenceReady {
  baseReview?: PersonaBaseReview;
  facetReview?: PersonaFacetReview;
  version: 1; status: 'available' | 'not_started'; mode: 'verified_partial_evidence' | 'model_extracted'; executionPolicy?: 'extraction_only' | 'claim_review_sampled_audit_v1' | null;
  criterion: { kind: 'device_canonical_turns'; operator: '>'; threshold: 100 };
  ownerScope?: 'all_counted' | 'confirmed_primary' | 'primary_with_candidates';
  unit: 'per_device_canonical_terminal_identity' | 'per_device_confirmed_primary' | 'per_device_selected_primary'; confidence: null; accepted: false; groundTruth: false; asOf: string | null; producerVersions?: string[];
  allTargets: VerifiedEvidenceGroup; visible: VerifiedEvidenceGroup;
  frozenRound: { scope: 'frozen_round_all_targets'; asOf: string; createdAt: string; allTargets: VerifiedEvidenceGroup; visible?: VerifiedEvidenceGroup } | null;
  /** 例行轮次之后：每台设备当前复核对象的最新结论；没有例行快照时为 null。 */
  latest?: VerifiedEvidenceLatest | null;
}
export interface VerifiedEvidenceLatest {
  scope: 'latest_per_person'; asOf: string; createdAt: string;
  stoppedEarlierDevices: number; earliestUpdatedThrough: string | null; subjectChangedDevices: number; roundStates: Record<string, number>;
}
export type VerifiedSpeakerEvidence = VerifiedSpeakerEvidenceReady | { version: 1; status: 'unavailable' };

export type FacetKind = 'specific_interest' | 'usage_expectation' | 'habit' | 'interaction_preference';
export type FacetScope = 'single_event' | 'self_reported_recurring' | 'explicit_preference' | 'observed_pattern';
export type FacetTemporal = 'current' | 'past' | 'intended' | 'unspecified';
export interface PersonaFacets {
  version: 'persona-evidence-facets-v1'; subjectCount: number; semanticReview: 'not_performed' | 'complete'; confidence: null;
  review?: PersonaFacetReviewCounts;
  coverage: { expectedSubjects: number; processedSubjects: number; completeSubjects: number; failedSubjects: number; missingSubjects: number; legacySubjects: number };
  categories: {kind: FacetKind; category: string; subjects: number; supportSubjects: number; refuteSubjects: number; habitEvidenceSubjects: number; byTemporal: Record<FacetTemporal, number>; scopeSubjects: Record<FacetScope, number>}[];
}
